/**
 * Role type — display only. Never used for access decisions.
 */
export type Role = string;

/**
 * Screen codes as defined by the backend `screens.code` column.
 * These are the Layer 1 (global) permission identifiers.
 *
 * NOTE: Action-level permissions (create/edit/delete) format is TBD — awaiting
 * backend confirmation (D-07). Do NOT add a Permissions constants map until confirmed.
 */
export const Screens = {
  DASHBOARD: 'dashboard',
  PRODUCTS: 'products',
  ORDERS: 'orders',
  ANALYTICS: 'analytics',
  SETTINGS: 'settings',
  /** Organization module screens */
  ORGANIZATION: 'organization',
  ORGANIZATION_MEMBERS: 'organization_members',
  ORGANIZATION_ROLES: 'organization_roles',
  ORGANIZATION_SUBSIDIARIES: 'organization_subsidiaries',
  ORGANIZATION_SCREEN_ACCESS: 'organization_screen_access',
  ORGANIZATION_ABAC_POLICIES: 'organization_abac_policies',
  ORGANIZATION_ABAC_USERS: 'organization_abac_users',
  ORGANIZATION_SSO: 'organization_sso',
  /** Platform admin screens (system admin only) */
  PLATFORM_DATA_MANAGEMENT: 'platform_data_management',
  PLATFORM_PERMISSIONS: 'platform_permissions',
  PLATFORM_RESOURCES: 'platform_resources',
  PLATFORM_SCREENS: 'platform_screens',
  PLATFORM_SUBSIDIARIES: 'platform_subsidiaries',
  PLATFORM_USERS: 'platform_users',
  /** Configuration manager — admins always allowed; custom roles need
   *  explicit `configuration` (or `configuration:read`) permission. */
  CONFIGURATION: 'configuration',
  /** Content Generation — the `content_generation` screen seeded by
   *  identity-service, which renders the sidebar entry at `/content`. */
  CONTENT_GENERATION: 'content_generation',
  /** Data Sharing — tenant-admin page for shareable Google Ads connection links
   *  (`/subsidiary/data-sharing`). Backend screen is a SUBSIDIARY_ADMIN child of
   *  `subsidiary_settings`; actions reuse the `connections:*` permissions. */
  INTEGRATIONS_SHARING: 'integrations_sharing',
} as const;

export type Screen = (typeof Screens)[keyof typeof Screens];

/**
 * Maps frontend screen codes to backend resource names.
 * Used to resolve screen codes → resource names for resource:action permission checks.
 */
export const screenToResource: Record<string, string> = {
  [Screens.DASHBOARD]: 'dashboard',
  [Screens.PRODUCTS]: 'products',
  [Screens.ORDERS]: 'orders',
  [Screens.ANALYTICS]: 'analytics',
  [Screens.SETTINGS]: 'settings',
  [Screens.ORGANIZATION]: 'organization',
  [Screens.ORGANIZATION_MEMBERS]: 'members',
  [Screens.ORGANIZATION_ROLES]: 'roles',
  [Screens.ORGANIZATION_SUBSIDIARIES]: 'subsidiaries',
  [Screens.ORGANIZATION_SCREEN_ACCESS]: 'screen_access',
  [Screens.ORGANIZATION_ABAC_POLICIES]: 'abac_policies',
  [Screens.ORGANIZATION_ABAC_USERS]: 'abac_users',
  [Screens.ORGANIZATION_SSO]: 'sso',
  [Screens.PLATFORM_DATA_MANAGEMENT]: 'data_management',
  [Screens.PLATFORM_PERMISSIONS]: 'permissions',
  [Screens.PLATFORM_RESOURCES]: 'resources',
  [Screens.PLATFORM_SCREENS]: 'screens',
  [Screens.PLATFORM_SUBSIDIARIES]: 'subsidiaries',
  [Screens.PLATFORM_USERS]: 'users',
  [Screens.CONFIGURATION]: 'configuration',
  [Screens.CONTENT_GENERATION]: 'content_generation',
};
